/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 上传已编辑照片选择弹窗组件（公共）
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-02-10 14:14:46
 */

import React, { useState, useMemo, useEffect } from 'react'
import { Modal, Button, Checkbox, message, Select, Input } from 'antd'
import {
  ExclamationCircleOutlined,
  FolderOutlined,
  PictureOutlined,
  PlusOutlined
} from '@ant-design/icons'
import {
  PhotoItem,
  WatermarkExtractionStatus
} from '../../context/WatermarkBatchContext'
import useImageProcessUrl from '../../hooks/useImageProcessUrl'
import {
  useAppDispatch,
  useGroupID,
  useIsAdmin,
  useVip,
  usePayment
} from '../../../../../app/hooks'
import { fetchGroupFolders, createFolder } from '../../../../../actions/folder'
import { useUploadToFolderModalTrack } from '../../hooks/useUploadToFolderModalTrack'
import crown_svip_icon from '../../../../../images/crown-svip-icon.png'
import './UploadEditedPhotosModal.scss'

const { Option } = Select

interface FolderItem {
  folderID: number
  folderName: string
}

interface UploadEditedPhotosModalProps {
  visible: boolean
  photos: PhotoItem[]
  onCancel: () => void
  onConfirm: (
    selectedPhotos: PhotoItem[],
    folderID: number,
    folderName?: string
  ) => void
  onUploadAndDelete?: (
    selectedPhotos: PhotoItem[],
    folderID: number,
    folderName?: string
  ) => void
}

const UploadEditedPhotosModal: React.FC<UploadEditedPhotosModalProps> = ({
  visible,
  photos,
  onCancel,
  onConfirm,
  onUploadAndDelete
}) => {
  const dispatch = useAppDispatch()
  const groupID = useGroupID()
  const isAdmin = useIsAdmin()
  const { isSVip } = useVip()
  const payment = usePayment()
  const { trackUploadModal } = useUploadToFolderModalTrack()

  // 文件夹列表
  const [folders, setFolders] = useState<FolderItem[]>([])
  const [loadingFolders, setLoadingFolders] = useState(false)
  // 选中的文件夹ID（默认使用"上传的照片/视频"文件夹）
  const [selectedFolderID, setSelectedFolderID] = useState<number | null>(null)
  const defaultFolderName = '上传的照片/视频'
  // 新建文件夹相关状态
  const [showCreateFolderInput, setShowCreateFolderInput] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [creatingFolder, setCreatingFolder] = useState(false)
  // 上传并删除原图确认弹窗
  const [uploadDeleteVisible, setUploadDeleteVisible] = useState(false)
  const [uploadDeleteChecked, setUploadDeleteChecked] = useState(false)

  // 生成默认文件夹名称：批量编辑水印照片_YYYYMMDD
  const generateDefaultFolderName = (): string => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `批量编辑水印照片_${year}${month}${day}`
  }

  // 获取已编辑的照片（有 aiURL 且状态为编辑完成）
  const editedPhotos = useMemo(() => {
    return photos.filter(
      (photo) =>
        photo.aiURL &&
        photo.watermarkStatus !== WatermarkExtractionStatus.FAILED &&
        photo.watermarkStatus !== WatermarkExtractionStatus.EDIT_FAILED
    )
  }, [photos])

  const { buildThumbUrl } = useImageProcessUrl()
  const THUMB_SIZE = 147

  // 选中的照片 seq 集合
  const [selectedSeqs, setSelectedSeqs] = useState<Set<number>>(new Set())

  // 全选状态
  const [checkAll, setCheckAll] = useState(false)

  // 埋点：弹窗出现
  useEffect(() => {
    if (visible) {
      trackUploadModal({
        actionType: 'enterPage',
        number1: editedPhotos.length
      })
    }
  }, [visible])

  // 获取文件夹列表
  useEffect(() => {
    if (visible && groupID) {
      setLoadingFolders(true)
      dispatch(fetchGroupFolders({ groupID }))
        .then((folderList: FolderItem[]) => {
          if (folderList && Array.isArray(folderList)) {
            setFolders(folderList)
            // 查找默认文件夹"上传的照片/视频"
            const defaultFolder = folderList.find(
              (folder) => folder.folderName === defaultFolderName
            )
            if (defaultFolder) {
              setSelectedFolderID(defaultFolder.folderID)
            } else if (folderList.length > 0) {
              setSelectedFolderID(folderList[0].folderID)
            }
          }
        })
        .catch((error) => {
          console.error('获取文件夹列表失败:', error)
          message.error('获取文件夹列表失败')
        })
        .finally(() => {
          setLoadingFolders(false)
        })
    }
    if (!visible) {
      setShowCreateFolderInput(false)
      setNewFolderName('')
      setUploadDeleteVisible(false)
      setUploadDeleteChecked(false)
    }
  }, [visible, groupID, dispatch, defaultFolderName])

  // 初始化：默认全选
  React.useEffect(() => {
    if (visible && editedPhotos.length > 0) {
      const allSeqs = new Set(editedPhotos.map((photo) => photo.seq))
      setSelectedSeqs(allSeqs)
      setCheckAll(true)
    } else {
      setSelectedSeqs(new Set())
      setCheckAll(false)
    }
  }, [visible, editedPhotos])

  const handlePhotoToggle = (seq: number) => {
    const newSelectedSeqs = new Set(selectedSeqs)
    if (newSelectedSeqs.has(seq)) {
      newSelectedSeqs.delete(seq)
    } else {
      newSelectedSeqs.add(seq)
    }
    setSelectedSeqs(newSelectedSeqs)
    setCheckAll(newSelectedSeqs.size === editedPhotos.length)
  }

  const handleCheckAll = (checked: boolean) => {
    if (checked) {
      const allSeqs = new Set(editedPhotos.map((photo) => photo.seq))
      setSelectedSeqs(allSeqs)
      setCheckAll(true)
    } else {
      setSelectedSeqs(new Set())
      setCheckAll(false)
    }
  }

  const handleConfirm = () => {
    if (!isSVip) {
      onCancel()
      payment.openSvip('webBatchEditWatermarkUpload', false, true, false)
      return
    }

    if (selectedSeqs.size === 0) {
      message.warning('请至少选择一张照片')
      return
    }

    if (!selectedFolderID) {
      message.warning('请选择上传的文件夹')
      return
    }

    const selectedPhotos = editedPhotos.filter((photo) =>
      selectedSeqs.has(photo.seq)
    )
    trackUploadModal({
      actionType: 'confirmUpload',
      number1: editedPhotos.length,
      photoNum: selectedPhotos.length
    })
    const folderName = folders.find(
      (f) => f.folderID === selectedFolderID
    )?.folderName
    onConfirm(selectedPhotos, selectedFolderID, folderName)
  }

  const handleUploadAndDelete = () => {
    if (!isAdmin) {
      Modal.info({
        title: '仅管理员可操作',
        okText: '我知道了'
      })
      return
    }
    if (!isSVip) {
      onCancel()
      payment.openSvip('webBatchEditWatermarkUpload', false, true, false)
      return
    }

    if (selectedSeqs.size === 0) {
      message.warning('请至少选择一张照片')
      return
    }

    if (!selectedFolderID) {
      message.warning('请选择上传的文件夹')
      return
    }

    setUploadDeleteChecked(false)
    trackUploadModal({
      actionType: 'clickUploadAndDelete',
      number1: editedPhotos.length
    })
    setUploadDeleteVisible(true)
  }

  const handleUploadDeleteCancel = () => {
    setUploadDeleteVisible(false)
    setUploadDeleteChecked(false)
  }

  const handleUploadDeleteConfirm = () => {
    if (!uploadDeleteChecked) {
      return
    }
    const selectedPhotos = editedPhotos.filter((photo) =>
      selectedSeqs.has(photo.seq)
    )
    trackUploadModal({
      actionType: 'confirmUploadAndDelete',
      number1: editedPhotos.length,
      photoNum: selectedPhotos.length
    })
    setUploadDeleteVisible(false)
    setUploadDeleteChecked(false)

    if (!selectedFolderID) {
      message.warning('请选择上传的文件夹')
      return
    }
    if (!onUploadAndDelete) {
      return
    }
    const folderName = folders.find(
      (f) => f.folderID === selectedFolderID
    )?.folderName
    onUploadAndDelete(selectedPhotos, selectedFolderID, folderName)
  }

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      message.warning('请输入文件夹名称')
      return
    }

    if (!groupID) {
      message.error('团队ID不存在')
      return
    }

    setCreatingFolder(true)
    try {
      const result = await dispatch(
        createFolder({
          groupID,
          folderName: newFolderName.trim(),
          parentFolderID: 0,
          source: 7
        })
      )

      if (result && result.folderID) {
        message.success('文件夹创建成功')
        trackUploadModal({
          actionType: 'createFolder',
          number1: editedPhotos.length
        })
        const folderList = await dispatch(fetchGroupFolders({ groupID }))
        if (folderList && Array.isArray(folderList)) {
          setFolders(folderList)
          setSelectedFolderID(result.folderID)
        }
        setShowCreateFolderInput(false)
        setNewFolderName('')
      } else {
        message.error('创建文件夹失败')
      }
    } catch (error) {
      console.error('创建文件夹失败:', error)
      message.error('创建文件夹失败，请稍后重试')
    } finally {
      setCreatingFolder(false)
    }
  }

  const handleClickCreateFolder = () => {
    if (!isAdmin) {
      Modal.info({
        title: '仅管理员可操作',
        okText: '我知道了'
      })
      return
    }
    setNewFolderName(generateDefaultFolderName())
    setShowCreateFolderInput(true)
  }

  const handleCancelCreateFolder = () => {
    setShowCreateFolderInput(false)
    setNewFolderName('')
  }

  const handleCancel = () => {
    trackUploadModal({
      actionType: 'cancel',
      number1: editedPhotos.length
    })
    onCancel()
  }

  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      title="选择要上传的已编辑照片"
      width={820}
      className="upload-edited-photos-modal"
      footer={
        <div className="upload-edited-photos-modal-footer">
          <div className="footer-info">
            已选择 {selectedSeqs.size} / {editedPhotos.length} 张
          </div>
          <div className="footer-actions">
            <Button onClick={handleCancel}>取消</Button>

            {isAdmin && (
              <div className="confirm-upload-btn-wrap">
                <img
                  className="confirm-upload-svip-badge"
                  src={crown_svip_icon}
                  width={24}
                  height={24}
                  alt="超级会员"
                />
                <Button onClick={handleUploadAndDelete}>
                  上传并删除工作圈内原图（Beta）
                </Button>
              </div>
            )}
            <div className="confirm-upload-btn-wrap">
              <img
                className="confirm-upload-svip-badge"
                src={crown_svip_icon}
                width={24}
                height={24}
                alt="超级会员"
              />
              <Button
                type="primary"
                onClick={handleConfirm}
                className="confirm-upload-btn"
              >
                确认上传
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <div className="upload-edited-photos-modal-content">
        <div className="folder-select-wrapper">
          <div className="folder-select-label">
            <FolderOutlined />
            <span>上传到文件夹：</span>
          </div>
          <div className="folder-select-group">
            <Select
              value={selectedFolderID}
              onChange={(value) => setSelectedFolderID(value)}
              loading={loadingFolders}
              className="folder-select"
              placeholder="请选择文件夹"
              disabled={loadingFolders || folders.length === 0}
            >
              {folders.map((folder) => (
                <Option key={folder.folderID} value={folder.folderID}>
                  {folder.folderName}
                </Option>
              ))}
            </Select>
            <Button
              type="link"
              icon={<PlusOutlined />}
              onClick={handleClickCreateFolder}
              className="create-folder-btn"
              disabled={showCreateFolderInput}
            >
              新建文件夹
            </Button>
          </div>
        </div>

        {showCreateFolderInput && (
          <div className="create-folder-input-wrapper">
            <Input
              placeholder="请输入文件夹名称"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onPressEnter={handleCreateFolder}
              disabled={creatingFolder}
              className="create-folder-input"
              autoFocus
            />
            <div className="create-folder-actions">
              <Button
                size="small"
                onClick={handleCancelCreateFolder}
                disabled={creatingFolder}
              >
                取消
              </Button>
              <Button
                type="primary"
                size="small"
                onClick={handleCreateFolder}
                loading={creatingFolder}
              >
                确定
              </Button>
            </div>
          </div>
        )}

        <div className="check-all-wrapper">
          <Checkbox
            checked={checkAll}
            onChange={(e) => handleCheckAll(e.target.checked)}
          >
            全选 ({editedPhotos.length} 张)
          </Checkbox>
        </div>

        <div className="photos-grid">
          {editedPhotos.map((photo) => {
            const isSelected = selectedSeqs.has(photo.seq)
            const imageUrl = buildThumbUrl(photo.aiURL, THUMB_SIZE)

            return (
              <div
                key={photo.seq}
                className={`photo-item ${isSelected ? 'selected' : ''}`}
                onClick={() => handlePhotoToggle(photo.seq)}
              >
                <div className="photo-thumbnail">
                  <img
                    src={imageUrl}
                    alt={`已编辑照片 ${photo.seq}`}
                    className="thumbnail-image"
                  />
                  <div className="photoCheckbox">
                    <Checkbox checked={isSelected} />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {editedPhotos.length === 0 && (
          <div className="empty-state">
            <PictureOutlined className="empty-icon" />
            <div className="empty-text">暂无已编辑的照片</div>
          </div>
        )}
      </div>
      <Modal
        open={uploadDeleteVisible}
        title="上传并删除原图（Beta）"
        onCancel={handleUploadDeleteCancel}
        onOk={handleUploadDeleteConfirm}
        okText="确认上传并删除原图"
        cancelText="取消"
        okButtonProps={{ disabled: !uploadDeleteChecked }}
        className="upload-delete-confirm-modal"
      >
        <div className="upload-delete-body">
          <p className="upload-delete-intro">
            本次将上传{' '}
            <strong>
              {editedPhotos.filter((p) => selectedSeqs.has(p.seq)).length}
            </strong>{' '}
            张已编辑照片到所选文件夹，上传成功后
            <strong>工作圈原图将被删除</strong>。
          </p>
          <div className="upload-delete-steps">
            <div className="upload-delete-steps-title">执行步骤：</div>
            <ol>
              <li>将已选中的已编辑照片上传至团队相册指定文件夹</li>
              <li>
                上传成功后，从当前工作区和工作圈中删除对应原图，原图只能照片回收站找回
              </li>
            </ol>
          </div>
          <div className="upload-delete-warning" role="alert">
            <ExclamationCircleOutlined aria-hidden />
            <div className="upload-delete-warning-content">
              <span className="upload-delete-warning-title">风险与须知：</span>
              <ul>
                <li>
                  原图删除后只能照片回收站找回，请务必在操作前确认已编辑照片的水印、时间、地点等信息正确。
                </li>
                <li>全部上传成功 → 才会调用删除原图接口</li>
                <li>本功能为 Beta 版本，如遇异常请及时反馈。</li>
              </ul>
            </div>
          </div>
          <Checkbox
            checked={uploadDeleteChecked}
            onChange={(e) => {
              const checked = e.target.checked
              setUploadDeleteChecked(checked)
              if (checked) {
                trackUploadModal({
                  actionType: 'confirmAgreement',
                  number1: editedPhotos.length
                })
              }
            }}
            className="upload-delete-checkbox"
          >
            我已阅读上述风险，并确认所选已编辑照片内容正确，自愿承担原图删除后果
          </Checkbox>
        </div>
      </Modal>
    </Modal>
  )
}

export default UploadEditedPhotosModal
